import type { ReactElement, ReactNode } from "react"

import { useIsDesktop } from "@/hooks/use-is-desktop"
import { cn } from "@/lib/utils"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"

export type ResponsiveDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  /** Element that opens the dialog (rendered in place of a default button). */
  trigger?: ReactElement
  children: ReactNode
  footer?: ReactNode
  /** Extra classes for the Dialog / Sheet popup. */
  className?: string
  showCloseButton?: boolean
}

/**
 * A centered `Dialog` on desktop and a bottom `Sheet` in the phone layout,
 * with the same title, body and footer.
 */
export function ResponsiveDialog({
  open,
  onOpenChange,
  title,
  trigger,
  children,
  footer,
  className,
  showCloseButton = true,
}: ResponsiveDialogProps) {
  const isDesktop = useIsDesktop()

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        {trigger && <DialogTrigger render={trigger} />}
        <DialogContent className={className} showCloseButton={showCloseButton}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          {children}
          {footer && <DialogFooter>{footer}</DialogFooter>}
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {trigger && <SheetTrigger render={trigger} />}
      <SheetContent
        side="bottom"
        showCloseButton={showCloseButton}
        className={cn(
          "max-h-[90dvh] rounded-t-[var(--surface-radius)] border-t-0 pb-[env(safe-area-inset-bottom)]",
          className
        )}
      >
        <SheetHeader className="pt-6.5 pb-4">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6">{children}</div>
        {footer && <SheetFooter>{footer}</SheetFooter>}
      </SheetContent>
    </Sheet>
  )
}
